import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Dialog,
  DialogContent,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SidebarLayout from "../sections/sidebar/SidebarLayout";
import TitleSection from "../sections/TitleSection";
import LeftNavigation from "../sections/NavigationSection";
import InfoNotification from "../modules/InfoNotification";
import QuickCostButton from "../modules/QuickCostButton";
import CircularLoading from "../modules/CircularLoading";
import FormulationTable from "../modules/FormulationTable";
import ResultsSection from "../sections/ResultsSection";
import { CookiesQuestions } from "../../features/fb/cookies/questions";
import { CookiesPlayBook } from "../../features/fb/cookies/playbook";
import ProgressiveAnalysisTracker from "../../features/fb/cookies/ProgressiveAnalysisTracker";
import { getFunctionalGroups, getTowerConfig } from "../../utils/commodityConfig";
import { apiFetch } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { theme } from "../../design/theme";
import { isValidFbProduct, normalizeFormulationRows, buildFormulationJsonFromRows, recipeValidationError, validatedFbShouldCost } from "../../features/fb/cookies/fbCostingDraft";

const { perlColors: assumptionColors, perlTypography } = theme.custom;

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "packaging", "ohp"];
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
const parseCostingEvent = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    throw new Error("The costing response could not be read. No price was accepted. Please retry.");
  }
};
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  packaging: "Packaging",
  ohp: "Overhead & Profits",
  logistics: "Logistics",
};

const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    return Number.isFinite(Number(value));
  });

  entries.sort((a, b) => {
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });

  return entries.map(([key, value]) => ({
    key,
    label: BREAKDOWN_LABELS[key] || toLabel(key),
    value: Number(value) || 0,
  }));
};

function renderAssumptions({ assumptions = {} }) {
  const preferredOrder = ["Effective costing inputs", "Reference data", "Formulation", "Process", "Packaging", "OHP", "Playbook", "Risk", "Substitutes"];
  const sections = Object.entries(assumptions || {})
    .filter(([, items]) => Array.isArray(items) && items.some((item) => item !== null && item !== ""))
    .sort(([a], [b]) => {
      const ai = preferredOrder.indexOf(a);
      const bi = preferredOrder.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return String(a).localeCompare(String(b));
    });

  if (sections.length === 0) {
    return null;
  }
  const flatItems = sections.flatMap(([, items]) => items);

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          bgcolor: assumptionColors.surface,
          border: `1px solid ${assumptionColors.border}`,
          borderRadius: 2,
          mt: 3,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "18px",
            fontFamily: perlTypography.family,
            letterSpacing: "0.3px",
            color: assumptionColors.textPrimary,
          }}
          >
          Assumptions
        </Typography>

        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {flatItems.map((assumption, idx) => (
            <Typography
              key={`assumption-${idx}`}
              component="li"
              variant="body2"
              sx={{
                mb: 1,
                fontSize: "14px",
                fontFamily: perlTypography.family,
                color: assumptionColors.textSecondary,
                lineHeight: "20px",
                letterSpacing: "0.3px",
              }}
            >
              {assumption}
            </Typography>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

const extractArrayFromUnknown = (value, preferredKeys = []) => {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return [];
    try {
      const parsed = JSON.parse(text);
      return extractArrayFromUnknown(parsed, preferredKeys);
    } catch {
      return [];
    }
  }

  if (value && typeof value === "object") {
    for (const key of preferredKeys) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const extracted = extractArrayFromUnknown(value[key], preferredKeys);
        if (extracted.length > 0) return extracted;
      }
    }

    for (const nested of Object.values(value)) {
      const extracted = extractArrayFromUnknown(nested, preferredKeys);
      if (extracted.length > 0) return extracted;
    }
  }

  return [];
};

const normalizeAssumptionSectionName = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const lookup = {
    playbookdata: "Playbook",
    playbook: "Playbook",
    riskdata: "Risk",
    risk: "Risk",
    substitutedata: "Substitutes",
    substitutes: "Substitutes",
    substitute: "Substitutes",
    formulationjson: "Formulation",
    formulation: "Formulation",
    processjson: "Process",
    process: "Process",
    packagingjson: "Packaging",
    packaging: "Packaging",
    ohpjson: "OHP",
    ohp: "OHP",
  };

  const normalizedKey = text.replace(/[_\s]+/g, "").toLowerCase();
  if (lookup[normalizedKey]) {
    return lookup[normalizedKey];
  }

  return text.endsWith("Json") ? text.slice(0, -4) : text;
};

const normalizeAssumptionsData = (value) => {
  if (!value) return null;

  let parsed = value;
  if (typeof parsed === "string") {
    const text = parsed.trim();
    if (!text) return null;
    try {
      parsed = JSON.parse(text);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }

  const normalized = Object.entries(parsed).reduce((acc, [sectionName, items]) => {
    const normalizedItems = Array.from(
      new Set(
        extractArrayFromUnknown(items, [sectionName, "Assumptions"])
          .map((item) => String(item ?? "").trim())
          .filter(Boolean),
      ),
    );
    if (normalizedItems.length > 0) {
      acc[sectionName] = normalizedItems;
    }
    return acc;
  }, {});

  return Object.keys(normalized).length > 0 ? normalized : null;
};

const collectAssumptionsFromResults = (...sources) => {
  const collected = {};

  const appendSectionItems = (sectionName, items) => {
    const normalizedSectionName = normalizeAssumptionSectionName(sectionName);
    if (!normalizedSectionName) return;

    const normalizedItems = extractArrayFromUnknown(items, [
      "Assumptions",
      sectionName,
      normalizedSectionName,
    ])
      .map((item) => String(item ?? "").trim())
      .filter(Boolean);

    if (!normalizedItems.length) return;
    if (!collected[normalizedSectionName]) {
      collected[normalizedSectionName] = [];
    }
    collected[normalizedSectionName].push(...normalizedItems);
    collected[normalizedSectionName] = Array.from(new Set(collected[normalizedSectionName]));
  };

  sources.forEach((source) => {
    if (!source || typeof source !== "object") return;

    Object.entries(source).forEach(([schemaName, schemaValue]) => {
      let parsedValue = schemaValue;
      if (typeof parsedValue === "string") {
        const text = parsedValue.trim();
        if (!text) return;
        try {
          parsedValue = JSON.parse(text);
        } catch {
          return;
        }
      }

      if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
        return;
      }

      if (String(schemaName).trim().toLowerCase() === "assumptions") {
        const normalizedTopLevelAssumptions = normalizeAssumptionsData(parsedValue);
        if (normalizedTopLevelAssumptions) {
          Object.entries(normalizedTopLevelAssumptions).forEach(([sectionName, items]) => {
            appendSectionItems(sectionName, items);
          });
        }
        return;
      }

      if (Array.isArray(parsedValue.Assumptions)) {
        appendSectionItems(schemaName, parsedValue.Assumptions);
      }
    });
  });

  return normalizeAssumptionsData(collected);
};

const shouldExcludeFrontendSchema = (schemaType) => {
  const value = String(schemaType || "")
    .trim()
    .toLowerCase();
  return (
    value === "derivedschemadefinitions" ||
    value === "derivedschemadefinition" ||
    value === "derivedschemadefinitionsjson" ||
    value === "derivedschemadefinitionjson" ||
    value === "derived_schema_definitions"
  );
};

const createGuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.random() * 16;
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return Math.floor(value).toString(16);
  });
};

const serializeComparisonValue = (value) => {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return String(value ?? "");
  }
};

export default function GenericCostingPage2() {
  const [searchParams] = useSearchParams();
  const tower = searchParams.get("tower") || "FB";
  const commodityParam = searchParams.get("commodity") || "Cookies";
  const commodity = commodityParam === "Ice Creams" ? "ICE CREAMS" : commodityParam;
  const { user } = useAuth();
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    surface: "#1e1f20",
    border: "rgba(255,255,255,0.12)",
    textPrimary: "#F1F2F3",
    textSecondary: "#A0A8AB",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};
  const towerConfig = useMemo(() => getTowerConfig(tower), [tower]);
  const functionalGroups = useMemo(() => getFunctionalGroups(tower, commodity), [tower, commodity]);

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });
  const [productData, setProductData] = useState(null);
  const [productDraft, setProductDraft] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [assumptionsData, setAssumptionsData] = useState(null);
  const [keyInsightsData, setKeyInsightsData] = useState(null);
  const [_frontendSchemaResults, setFrontendSchemaResults] = useState({});
  const [_schemaTypes, setSchemaTypes] = useState([]);
  const [_schemaDefinitionColumns, setSchemaDefinitionColumns] = useState([]);
  const [_schemaDefinitions, setSchemaDefinitions] = useState([]);
  const [_frontendReturnItems, setFrontendReturnItems] = useState([]);
  const [_requestPayload, setRequestPayload] = useState(null);
  const [_statusMessage, setStatusMessage] = useState("");
  const [quickCostError, setQuickCostError] = useState("");
  const [productImageError, setProductImageError] = useState("");
  const previousRunKeyRef = React.useRef(null);
  const previousRecipeSnapshotRef = React.useRef("");
  const previousPlaybookSnapshotRef = React.useRef("");
  const lastProductRef = React.useRef(null);
  const requestRevisionRef = React.useRef(0);
  const activeRequestRef = React.useRef(null);
  const imageRequestRef = React.useRef(null);
  const imageRetryRef = React.useRef(null);
  const [playBookOverrides, setPlayBookOverrides] = useState({});
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);
  const recipeError = previousRecipeSnapshotRef.current ? recipeValidationError(recipeData) : "";
  React.useEffect(() => () => {
    requestRevisionRef.current += 1;
    activeRequestRef.current?.abort();
    imageRequestRef.current?.abort();
  }, []);

  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, product: true }));
  };

  const invalidateQuote = React.useCallback(() => {
    requestRevisionRef.current += 1;
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
    imageRequestRef.current?.abort();
    imageRequestRef.current = null;
    imageRetryRef.current = null;
    setQuickCostError("");
    setProductImageError("");
    setIsProcessing(false);
    setCostData(null);
    setRiskData(null);
    setSubstituteData(null);
    setProductImageUrl(null);
    setGeneratingImage(false);
    setAssumptionsData(null);
    setKeyInsightsData(null);
    setRequestPayload(null);
    setShowProgressTracker(false);
  }, []);

  const handleProductComplete = React.useCallback((data) => {
    const next = isValidFbProduct(data, commodity) ? data : null;
    const key = serializeComparisonValue(next);
    if (lastProductRef.current !== key) {
      invalidateQuote();
      lastProductRef.current = key;
      previousRunKeyRef.current = null;
      previousRecipeSnapshotRef.current = "";
      previousPlaybookSnapshotRef.current = "";
      setRecipeData([]);
      setPlayBookData(null);
      setPlayBookOverrides({});
      setFrontendSchemaResults({});
      setFrontendReturnItems([]);
    }
    setProductData(next);
  }, [invalidateQuote, commodity]);

  const handlePlayBookChange = (field, value) => {
    invalidateQuote();
    if (["moisture_content", "process_loss", "packaging_loss"].includes(field)) {
      setPlayBookOverrides((prev) => ({ ...prev, [field]: value }));
    }
    setPlayBookData((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const syncFormulationJsonState = (updatedRows) => {
    invalidateQuote();
    const currentFormulationJson = extractArrayFromUnknown(_frontendSchemaResults?.FormulationJson, [
      "FormulationJson",
      "rows",
      "data",
    ]);
    const updatedFormulationJson = buildFormulationJsonFromRows(updatedRows, currentFormulationJson);

    setFrontendSchemaResults((prev) => ({
      ...(prev || {}),
      FormulationJson: updatedFormulationJson,
    }));
    setFrontendReturnItems((prev) =>
      Array.isArray(prev)
        ? prev.some((item) => item?.schema_type === "FormulationJson")
          ? prev.map((item) =>
              item?.schema_type === "FormulationJson"
                ? { ...item, result: updatedFormulationJson }
                : item,
            )
          : [...prev, { schema_type: "FormulationJson", result: updatedFormulationJson }]
        : [{ schema_type: "FormulationJson", result: updatedFormulationJson }],
    );
  };

  const runDeferredImageGeneration = async (basePayload, completeResponse, revision) => {
    if (revision !== requestRevisionRef.current) return;
    if (!basePayload?.tower || !basePayload?.commodity) return;
    if (imageRequestRef.current) return;
    imageRetryRef.current = { basePayload, completeResponse, revision };
    const imageController = new AbortController();
    imageRequestRef.current = imageController;

    const basePayloadInputs =
      basePayload?.payload_inputs && typeof basePayload.payload_inputs === "object"
        ? basePayload.payload_inputs
        : {};
    const responsePlaybook =
      completeResponse?.playBookData?.playBookData ||
      completeResponse?.playBookData ||
      playBookData ||
      null;
    const primaryPackaging =
      responsePlaybook?.primary_packaging ||
      responsePlaybook?.primaryPackaging ||
      basePayloadInputs?.primaryPackaging ||
      basePayloadInputs?.primary_packaging ||
      basePayload?.primaryPackaging ||
      "";

    const imagePayload = {
      ...basePayload,
      playBookData: responsePlaybook || undefined,
      primaryPackaging: primaryPackaging || undefined,
      payload_inputs: {
        ...basePayloadInputs,
        primaryPackaging: primaryPackaging || basePayloadInputs?.primaryPackaging || undefined,
      },
    };

    setGeneratingImage(true);
    setProductImageError("");
    try {
      const imageResponse = await apiFetch("/api/fb/generic2/quick-cost-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
        signal: imageController.signal,
      });

      const imageData = await imageResponse.json();
      if (!imageResponse.ok) {
        throw new Error(imageData?.message || imageData?.error || `HTTP ${imageResponse.status}`);
      }

      if (revision !== requestRevisionRef.current) return;
      const imageResult = imageData?.productImage || {};
      if (String(imageData?.status || "").toLowerCase() === "error"
          || typeof imageResult.imageUrl !== "string" || !imageResult.imageUrl.trim()) {
        throw new Error("No usable product image was returned");
      }
      setProductImageUrl(imageResult.imageUrl);
    } catch (error) {
      if (revision !== requestRevisionRef.current || error?.name === "AbortError") return;
      setProductImageError(error?.status === 401 ? error.message
        : "The product image could not be generated. The calculated cost is unchanged.");
    } finally {
      if (imageRequestRef.current === imageController) imageRequestRef.current = null;
      if (revision === requestRevisionRef.current) setGeneratingImage(false);
    }
  };

  const retryProductImage = () => {
    const last = imageRetryRef.current;
    if (last && last.revision === requestRevisionRef.current) {
      runDeferredImageGeneration(last.basePayload, last.completeResponse, last.revision);
    }
  };

  const applyQuickCostResponse = (data) => {
    const fetchedFrontendReturnItemsRaw = Array.isArray(data?.frontend_return_items)
      ? data.frontend_return_items
      : [];
    const fetchedFrontendReturnItems = fetchedFrontendReturnItemsRaw.filter(
      (item) => !shouldExcludeFrontendSchema(item?.schema_type),
    );
    const schemaResultsMap = fetchedFrontendReturnItems.reduce((acc, item) => {
      if (item?.schema_type) {
        acc[item.schema_type] = item.result;
      }
      return acc;
    }, {});

    const returnedShouldCost = schemaResultsMap.ShouldCostResult || data.ShouldCostResult;
    const exactFbScope = tower.toUpperCase() === "FB" && ["COOKIES", "ICECREAMS"].includes(commodity.replace(/[\s_-]/g, "").toUpperCase());
    const shouldCostRaw = exactFbScope ? validatedFbShouldCost(returnedShouldCost) : returnedShouldCost;

    setFrontendReturnItems(fetchedFrontendReturnItems);
    setFrontendSchemaResults(schemaResultsMap);

    const formulationSources = [
      data?.RecipeData,
      data?.FormulationJson,
      schemaResultsMap.FormulationJson,
      schemaResultsMap.formulationData,
    ];
    let rawFormulationRows = [];
    for (const source of formulationSources) {
      rawFormulationRows = extractArrayFromUnknown(source, [
        "FormulationJson",
        "formulationData",
        "rows",
        "data",
      ]);
      if (rawFormulationRows.length > 0) break;
    }

    const formulationRows = normalizeFormulationRows(rawFormulationRows);
    if (formulationRows.length > 0) {
      setRecipeData(formulationRows);
      setSectionStarted((prev) => ({ ...prev, formulation: true }));
    }
    previousRecipeSnapshotRef.current = serializeComparisonValue(formulationRows);

    const playbookRaw =
      schemaResultsMap.playBookData || schemaResultsMap.PlayBookData || data.playBookData || data.costingAssumptions;
    if (playbookRaw) {
      const normalizedPlaybook = { ...(playbookRaw.playBookData || playbookRaw), ...(data.costingAssumptions || {}) };
      setPlayBookData(normalizedPlaybook);
      setSectionStarted((prev) => ({ ...prev, playbook: true }));
      previousPlaybookSnapshotRef.current = serializeComparisonValue(normalizedPlaybook);
    }

    const riskRaw = schemaResultsMap.riskData || schemaResultsMap.RiskData || data.riskData;
    if (riskRaw) {
      setRiskData(riskRaw.riskData || riskRaw);
    }

    const substituteRaw =
      schemaResultsMap.substituteData || schemaResultsMap.SubstituteData || data.substituteData;
    if (substituteRaw) {
      setSubstituteData(substituteRaw.substituteData || substituteRaw);
    }

    // The server includes the effective priced inputs and labels AI narrative.
    // Recollecting raw schema notes would reintroduce unqualified contradictions.
    const normalizedAssumptions = normalizeAssumptionsData(data.assumptions)
      || collectAssumptionsFromResults(schemaResultsMap, data);
    if (normalizedAssumptions) {
      setAssumptionsData(normalizedAssumptions);
    }

    const keyInsightsRaw = schemaResultsMap.KeyInsightsJson || data.KeyInsightsJson;
    if (keyInsightsRaw) {
      setKeyInsightsData(keyInsightsRaw.key_insights || keyInsightsRaw);
    }

    const imageRaw = schemaResultsMap.productImage || data.productImage;
    if (imageRaw?.imageUrl) {
      setProductImageUrl(imageRaw.imageUrl);
    }

    if (shouldCostRaw) {
      setCostData({
        breakdown: {
          material: shouldCostRaw.MaterialCost || 0,
          process: shouldCostRaw.ProcessCost || 0,
          packaging: shouldCostRaw.PackagingCost || 0,
          ohp: shouldCostRaw.OHPCost || 0,
          total_should_cost: shouldCostRaw.TotalCost || 0,
        },
        total: shouldCostRaw.TotalCost || 0,
      });
    }

    setStatusMessage(`Fetched results for ${tower}/${commodity}.`);

    const hasResultData =
      !!shouldCostRaw ||
      !!riskRaw ||
      !!substituteRaw ||
      !!keyInsightsRaw ||
      !!normalizedAssumptions ||
      !!imageRaw ||
      formulationRows.length > 0;

    setSectionStarted((prev) => ({
      ...prev,
      formulation: prev.formulation || formulationRows.length > 0,
      playbook: prev.playbook || !!playbookRaw,
      results: prev.results || hasResultData,
    }));
  };

  const handleQuickCost = async () => {
    if (!isValidFbProduct(productData, commodity)) {
      alert("Please complete product information first");
      return;
    }

    const payloadBase = {
      tower,
      commodity,
      productName: productData?.productName || "",
      country: productData?.manufacturingCountry || "",
      skuWeight: parseFloat(productData?.skuWeight) || 0,
      skuWeightUnit: productData.skuWeightUnit,
      marketPositioning: productData?.marketPositioning || "",
      annualVolume: productData?.annualVolume || "",
    };
    const runKeyString = JSON.stringify(payloadBase);
    const isReRun = previousRunKeyRef.current === runKeyString;
    const primaryPackaging =
      playBookData?.primary_packaging || playBookData?.primaryPackaging || "";
    const normalizedRecipe = Array.isArray(recipeData)
      ? recipeData.filter((row) => row && (row.ingredient || row.percentage))
      : [];
    const normalizedPlaybook = playBookData && typeof playBookData === "object" ? playBookData : null;
    const skipFormulationAi =
      isReRun &&
      previousRecipeSnapshotRef.current !== "" &&
      serializeComparisonValue(normalizedRecipe) !== previousRecipeSnapshotRef.current;
    const skipPlayBookAi =
      isReRun &&
      previousPlaybookSnapshotRef.current !== "" &&
      serializeComparisonValue(normalizedPlaybook) !== previousPlaybookSnapshotRef.current;
    const previousSchemaResults =
      _frontendSchemaResults && typeof _frontendSchemaResults === "object" ? _frontendSchemaResults : {};
    if (skipFormulationAi) {
      const error = recipeValidationError(normalizedRecipe);
      if (error) {
        alert(error);
        return;
      }
    }
    const revision = ++requestRevisionRef.current;
    activeRequestRef.current?.abort();
    imageRequestRef.current?.abort();
    imageRequestRef.current = null;
    imageRetryRef.current = null;
    const controller = new AbortController();
    activeRequestRef.current = controller;

    setIsProcessing(true);
    setQuickCostError("");
    setProductImageError("");
    setStatusMessage("");
    if (!isReRun) {
      setRecipeData([]);
      setPlayBookData(null);
      setFrontendSchemaResults({});
      setFrontendReturnItems([]);
    }
    setCostData(null);
    setRiskData(null);
    setSubstituteData(null);
    setProductImageUrl(null);
    setGeneratingImage(false);
    setAssumptionsData(null);
    setKeyInsightsData(null);
    setSchemaDefinitionColumns([]);
    setSchemaDefinitions([]);
    setProgressSteps([]);
    setCurrentProgressStep(0);
    setOverallProgress(0);
    setShowProgressTracker(true);

    try {
      const metadataPayload = {
        userEmail: user?.email || "",
        projectId: createGuid(),
        partKey: `PART_${Date.now()}`,
        componentId: "Comp1",
        isReRun,
      };

      const payloadInputs = {
        ...payloadBase,
        primaryPackaging: primaryPackaging || undefined,
        existingRecipe: normalizedRecipe.length > 0 ? normalizedRecipe : undefined,
        playBookData: normalizedPlaybook || undefined,
        playBookOverrides,
        previousSchemaResults: Object.keys(previousSchemaResults).length > 0 ? previousSchemaResults : undefined,
        skipFormulationAi,
        skipPlayBookAi,
      };

      const payload = {
        tower,
        commodity,
        metadata: metadataPayload,
        payload_inputs: payloadInputs,
        primaryPackaging: primaryPackaging || undefined,
      };

      setRequestPayload(payload);

      const response = await apiFetch("/api/fb/generic2/quick-cost-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (revision !== requestRevisionRef.current) return;

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Streaming response is not available");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let completed = false;
      let streamTotalSteps = 1;
      const successfulSteps = new Set();

      while (!completed) {
        const { done, value } = await reader.read();
        if (revision !== requestRevisionRef.current) return;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.trim()) continue;
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          const eventData = parseCostingEvent(dataMatch[1]);
          if (eventData.type === "start") {
            const totalSteps = eventData.total_steps || 1;
            streamTotalSteps = totalSteps;
            successfulSteps.clear();
            const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
              id: `step-${i + 1}`,
              label: `Step ${i + 1}`,
              status: "pending",
              message: "",
            }));
            setProgressSteps(initialSteps);
            setCurrentProgressStep(1);
            continue;
          }

          if (eventData.type === "progress") {
            const step = Number(eventData.step) || 1;
            const total = Number(eventData.total) || 1;
            const status = String(eventData.status || "").toLowerCase();
            const mappedStatus =
              status === "running"
                ? "in-progress"
                : status === "success"
                  ? "completed"
                  : status === "skipped"
                    ? "completed"
                    : status === "error"
                      ? "error"
                      : status;

            setProgressSteps((prev) => {
              const updated = [...prev];
              const idx = step - 1;
              if (updated[idx]) {
                updated[idx] = {
                  ...updated[idx],
                  label: eventData.label || updated[idx].label,
                  status: mappedStatus,
                  message: eventData.message || "",
                  error: eventData.error,
                };
              }
              return updated;
            });

            if (status === "running" || status === "success" || status === "skipped") {
              setCurrentProgressStep(step);
            }

            if (status === "success" || status === "skipped") {
              successfulSteps.add(step);
              const pct = (successfulSteps.size / total) * 100;
              setOverallProgress((prev) => (pct > prev ? pct : prev));
            }
            continue;
          }

          if (eventData.type === "complete") {
            if (revision !== requestRevisionRef.current) return;
            completed = true;
            if (eventData.status === "error") {
              throw new Error(eventData.message || "Quick Cost failed");
            }

            applyQuickCostResponse(eventData);
            previousRunKeyRef.current = runKeyString;
            const finalPct = (successfulSteps.size / streamTotalSteps) * 100;
            setOverallProgress((prev) => (finalPct > prev ? finalPct : prev));
            setProgressSteps((prev) =>
              prev.map((item) => ({
                ...item,
                status: item.status === "error" ? "error" : "completed",
                message: item.status === "error" ? item.message || "Failed" : "Complete",
              })),
            );
            const payloadForImage = { ...payload };
            const completeResponse = eventData;
            setTimeout(() => {
              if (revision !== requestRevisionRef.current) return;
              setShowProgressTracker(false);
              setActiveSection("results");
              runDeferredImageGeneration(payloadForImage, completeResponse, revision);
            }, 1200);
          }
        }
      }
      if (!completed) {
        throw new Error("The costing response ended before completion. No price was generated. Please retry.");
      }
    } catch (error) {
      if (revision !== requestRevisionRef.current || error?.name === "AbortError") return;
      const message = error?.message || "Failed to call backend";
      setQuickCostError(message);
      setStatusMessage(`Quick Cost failed: ${message}`);
      alert(`Quick Cost failed: ${message}`);
      setShowProgressTracker(false);
    } finally {
      if (revision === requestRevisionRef.current) {
        setIsProcessing(false);
        activeRequestRef.current = null;
      }
    }
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description: `Answer a few quick questions about your ${commodity.toLowerCase()} product to run a quick backend schema query.`,
    },
    {
      id: "formulation",
      label: "Formulation",
      icon: <ScienceIcon />,
      disabled: !sectionStarted.product,
      title: "Ready to Build Formulation",
      description: "Placeholder section for formulation output.",
    },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product,
      title: "Ready to Configure PlayBook",
      description: "Placeholder section for playbook output.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product,
      title: "Analysis Report",
      description: "Shows schema types returned by fb_generic_routes2.",
    },
  ];

  const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total ?? costData?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);

  const hasResultsData =
    !!costData ||
    !!riskData ||
    !!substituteData ||
    !!productImageUrl ||
    !!assumptionsData ||
    !!keyInsightsData;

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName={towerConfig.name}
          industry={towerConfig.industry}
          commodity={commodity}
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {quickCostError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {quickCostError} Review your inputs or use Quick Cost to try again.
              </Alert>
            )}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {sectionStarted.product && activeSection === "product" && (
              <Box sx={{ height: "92%" }}>
                <CookiesQuestions
                  onComplete={handleProductComplete}
                  onDataChange={handleProductComplete}
                  onDraftChange={setProductDraft}
                  initialData={productDraft || productData}
                  commodity={commodity}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                {recipeError && <Typography role="alert" color="error" sx={{ mb: 2 }}>{recipeError}</Typography>}
                {recipeData.length > 0 ? (
                  <FormulationTable
                    data={recipeData}
                    onRowChange={(index, field, value) => {
                      const updated = [...recipeData];
                      updated[index][field] = value;
                      setRecipeData(updated);
                      syncFormulationJsonState(updated);
                    }}
                    onRowAdd={() => {
                      const updated = [
                        ...recipeData,
                        { ingredient: "", percentage: 0, functional_group: null },
                      ];
                      setRecipeData(updated);
                      syncFormulationJsonState(updated);
                    }}
                    onRowDelete={(index) => {
                      const updated = recipeData.filter((_, i) => i !== index);
                      setRecipeData(updated);
                      syncFormulationJsonState(updated);
                    }}
                    functionalGroups={functionalGroups}
                    brandColor={towerConfig.brandColor}
                    editable={true}
                    showTotal={true}
                    compositionTolerance={recipeData.length * 0.005 + 1e-8}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    No formulation output returned yet.
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <CookiesPlayBook
                    data={playBookData}
                    onChange={handlePlayBookChange}
                    tower={tower}
                    commodity={commodity}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    No playbook output returned yet.
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {hasResultsData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    imageError={productImageError}
                    onRetryImage={retryProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor={towerConfig.brandColor}
                    assumptions={assumptionsData || {}}
                    keyInsights={keyInsightsData || null}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: assumptionsData || {},
                    })}
                  />
                ) : null}
              </Box>
            )}
          </Box>
        </Box>

        {sectionStarted.product && (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!isValidFbProduct(productData, commodity) || Boolean(recipeError)}
            loading={isProcessing}
            brandColor={towerConfig.brandColor}
          />
        )}

        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
            <Button fullWidth variant="contained" onClick={() => {
              invalidateQuote();
              setQuickCostError("Stopped waiting for this result.");
            }}>
              Stop waiting
            </Button>
          </DialogContent>
        </Dialog>

        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message="Querying schema types..."
          subtext={`${tower} / ${commodity}`}
        />
      </Box>
    </SidebarLayout>
  );
}
